import { Autocomplete, Divider, Paper, TextField, Drawer, Box } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm, UseFormReturn } from "react-hook-form";
import { useDependencyInjection } from "../../config/di/dependency_injection_context";
import { CentroCustoDropDown } from "../domain/models/centro_custo_dropdown";
import { FormRadioGroupInput } from "./masked_inputs/react_forms/form_radio_group_input";
import { FormPercentageMaskedInput } from "./masked_inputs/react_forms/form_percentage_masked_input";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  CentroCustoForm,
  CentroCustoSchema,
  defaultCentroCustoForm,
} from "../../features/comercial/presentation/schemas/CentroCusto_cadastros/centroCusto";

import { usePageLoader } from "../contexts/page_content_context";

import DynamicIcon from "./dynamic_icon";
import DefaultFormInput from "./default_form_input";
import PrimaryButton from "./primary_button";

type Props<F extends {}> = {
  name: keyof F;
  label?: string;
  newButtonLabel?: string;
  required?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  reactHookForm: UseFormReturn<F>;
  IdContrato?: number;
};

export const DropDownCentroCusto = <F extends {}>({
  name,
  newButtonLabel,
  required,
  readOnly,
  placeholder,
  reactHookForm,
  IdContrato = 0,
}: Props<F>) => {
  const [openModal, setOpenModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const { pushErrorNotification, pushInfoNotification } = usePageLoader();

  const [centrocustoDropDown, setCentroCustoDropDown] = useState<CentroCustoDropDown[]>([]);

  const {
    commons: { getCentroCustoDropDownUseCase },
    comercial: { createCentroCustoUseCase },
  } = useDependencyInjection();

  const hookForm = useForm<CentroCustoForm>({
    reValidateMode: "onSubmit",
    mode: "onSubmit",
    defaultValues: defaultCentroCustoForm,
    resolver: zodResolver(CentroCustoSchema),
  });

  const loadCentroCustoDropDown = useCallback(async () => {
    try {
      const centroCusto = await getCentroCustoDropDownUseCase.getCentroCustoDropDown(IdContrato);
      setCentroCustoDropDown(centroCusto);
      if (IdContrato > 0 && centroCusto.length > 0) {
        const primeiroItem = centroCusto[0];
        reactHookForm.setValue(name as any, primeiroItem.id as any);
      }
    } catch (e: unknown) {
      console.error("Erro ao carregar centro de custo:", e);
    }
  }, [IdContrato, getCentroCustoDropDownUseCase, name, reactHookForm]);

  //Create Centro de Custo

  const hanldleSalvarCentroCusto = useCallback(
    async (form: CentroCustoForm) => {
      try {
        setSaving(true);

        const newCentroCusto = await createCentroCustoUseCase.createCentroCusto(
          form.status,
          form.codigo,
          form.descricao,
          form.bdi,
          form.bdi2,
          form.numeroCno ?? undefined,
          form.obsCentroCusto ?? undefined,
        );

        reactHookForm.setValue(name as any, newCentroCusto.idCentroCusto as any);
        pushInfoNotification("SUCESSO", "Centro custo criado com sucesso!!", { isAutoFinish: true });
      } catch (e: unknown) {
        pushErrorNotification("Erro", ["Falha ao salvar centroCusto"], { isAutoFinish: true });
      } finally {
        setSaving(false);
        loadCentroCustoDropDown();
        setOpenModal(false);
      }
    },
    [createCentroCustoUseCase, loadCentroCustoDropDown, name, pushErrorNotification, pushInfoNotification, reactHookForm],
  );

  //Load Centro de Custo DropDown

  useEffect(() => {
    if (IdContrato > 0) {
      setCentroCustoDropDown([]);
    }
    loadCentroCustoDropDown();
  }, [IdContrato, loadCentroCustoDropDown]);

  // Add Centro Custo Button

  const CustomPaper = useMemo(
    () =>
      ({ children, ...props }: any) => (
        <Paper {...props}>
          <div>
            {children}
            <Divider />
            <div>
              <div
                className="py-2 px-4 cursor-pointer rounded-[4px] font-semibold flex items-center gap-2 text-sm text-primary-green hover:bg-hover-light-gray"
                onMouseDown={() => setOpenModal(true)}
                role="button"
                tabIndex={0}
              >
                <DynamicIcon nameIcon="Add" size={20} className="text-primary-green" />
                <span>{newButtonLabel ?? "Novo"}</span>
              </div>
            </div>
          </div>
        </Paper>
      ),
    [newButtonLabel],
  );

  return (
    <>
      {/*Centro Custo DropDown */}

      <Controller
        name={name as any}
        control={reactHookForm.control}
        render={({ field, formState }) => {
          let helperText: string | null = null;

          if (formState.errors[name as keyof typeof formState.errors]) {
            const fieldError = formState.errors[name] as any;
            if (fieldError?.message && typeof fieldError.message === "string") {
              helperText = fieldError.message;
            }
          }

          return (
            <Box sx={{ position: "relative" }}>
              <Autocomplete
                fullWidth={true}
                size="small"
                options={centrocustoDropDown}
                getOptionKey={opt => opt.id}
                getOptionLabel={(opt) => opt.title}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                slots={{
                  paper: CustomPaper,
                }}
                sx={{ minHeight: 45 }}
                readOnly={readOnly}
                disabled={formState.disabled}
                value={centrocustoDropDown.find((x) => x.id === field.value) ?? null}
                onChange={(_, newValue) => {
                  field.onChange(newValue ? newValue.id : undefined);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Centro de Custo"
                    error={helperText !== null}
                    helperText={helperText}
                    placeholder={placeholder}
                    required={required}
                  />
                )}
              />
            </Box>
          );
        }}
      />

      {/* Add Centro Custo Modal */}

      <Drawer
        anchor="right"
        open={openModal}
        onClose={() => setOpenModal(false)}
        style={{ zIndex: 1301 }}
        PaperProps={{ style: { width: 500, padding: 20, display: "flex", flexDirection: "column", height: "100%" } }}
      >
        <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
          <h2 className="text-lg font-semibold mb-4">Adicionar Centro de Custo</h2>

          <div className="flex flex-col items-start w-[25rem] gap-4">
            <FormRadioGroupInput
              name="status"
              label="Status"
              options={[
                { id: 0, label: "Ativo" },
                { id: 1, label: "Bloqueado" },
                { id: 2, label: "Encerrado" },
              ]}
              reactHookForm={hookForm}
            />

            <DefaultFormInput name="codigo" label="Código" required={true} reactHookForm={hookForm} />

            <DefaultFormInput name="descricao" label="Nome de Centro de Custo" required={true} reactHookForm={hookForm} />

            <FormPercentageMaskedInput name="bdi" label="BDI" required={false} reactHookForm={hookForm} />

            <FormPercentageMaskedInput name="bdi2" label="BDI 02" required={false} reactHookForm={hookForm} />

            <DefaultFormInput name="numeroCno" label="N° CNO" required={false} reactHookForm={hookForm} />
          </div>
        </Box>

        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            width: "100%",
            background: "white",
            padding: 2,
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box
            className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold"
            onClick={() => setOpenModal(false)}
          >
            <h1 className="text-sm text-black dark:text-white">Cancelar</h1>
          </Box>

          <Box width="120px">
            {saving ? (
              <PrimaryButton buttonColor="primary-green">Salvando...</PrimaryButton>
            ) : (
              <PrimaryButton buttonColor="primary-green" onClick={hookForm.handleSubmit(hanldleSalvarCentroCusto)}>
                Salvar
              </PrimaryButton>
            )}
          </Box>
        </Box>
      </Drawer>
    </>
  );
};
